// src/ui/steps/step0-scope.jsx: Kies type opdracht (film/festival) en daarna:
//   - film: zzp ('uitvoerder') of opdrachtgever
//   - festival: zzp of loondienst
// Interne substappen: 'a' = type-keuze, 'b' = vorm/perspectief-keuze (eigen scherm met terug-knop).
import React from "react";
import { FW, FwButton } from "../primitives";

export const StepScope = ({ state, setState, next }) => {
  // Open direct substap b wanneer het type al bekend is, bij terug-navigatie
  // vanuit step 1, of via een ?type=…-deeplink (dan staat alleen het type al vast).
  const [substap, setSubstap] = React.useState(state.type ? "b" : "a");

  // "Nieuwe berekening" wist de hele state terwijl deze component gemount blijft;
  // val dan terug naar de type-keuze i.p.v. te blijven hangen op substap b.
  React.useEffect(() => {
    if (!state.type) setSubstap("a");
  }, [state.type]);

  const pickType = (t) => {
    setState(s => {
      // Wissel van type → reset omschrijving, rollen en sub-keuzes
      const base = s.type === t
        ? { ...s, type: t }
        : { ...s, type: t, omschrijving: "", rollen: [], vorm: undefined, perspectief: undefined };
      return base;
    });
    // Beide types tonen substap b: film vraagt zzp/opdrachtgever, festival vraagt zzp/loondienst.
    setTimeout(() => setSubstap("b"), 150);
  };

  const pickPerspective = (p) => {
    // Film: zzp ('uitvoerder') vs opdrachtgever. Wisselen van perspectief is een
    // cruciale verandering, begin volledig fris: omschrijving, rollen,
    // AI-suggesties en alle ingevulde uren worden gewist.
    setState(s => {
      const changed = s.perspectief && s.perspectief !== p;
      if (changed) return { type: "film", perspectief: p };
      return { ...s, type: "film", perspectief: p, vorm: undefined };
    });
    setTimeout(next, 150);
  };

  const pickVorm = (v) => {
    // Festival: vorm-keuze impliceert "ik bereken voor mezelf".
    // Bij wisseling tussen zzp ↔ loondienst worden de werkdagen-/
    // dienstverband-state per rol gewist; de rol en zwaarteniveau blijven.
    setState(s => {
      const vormGewijzigd = s.vorm && s.vorm !== v;
      const rollen = vormGewijzigd
        ? (s.rollen || []).map(r => ({
            ...r,
            werkdagen: undefined,
            dienstverband: undefined,
            periodiek: undefined,
          }))
        : s.rollen;
      return { ...s, type: "festival", vorm: v, perspectief: "uitvoerder", rollen };
    });
    setTimeout(next, 150);
  };

  // Introtekst onder de hero verschilt per substap/type:
  //  - startpagina (a): algemene "let op"-tekst zonder opslagpercentage
  //  - film gekozen (b): zzp-opslag 74,45%
  //  - festival gekozen (b): zzp-opslag 69,26%
  // Tweede alinea verschilt per flow: film/AV benoemt "type productie" als
  // expliciet film/AV-voorbehoud; festival voegt "type productie" toe na de
  // complexiteit zonder dat voorbehoud.
  const ondergrensFilmAv = (
    <>Het starttarief vormt de <b>ondergrens</b> die past bij het niveau van de opdracht. Je <b>ervaring</b>, <b>reputatie</b> of <b>specialisme</b>, de <b>complexiteit</b> van de opdracht, en bij film- en AV-producties het <b>type productie</b>, kunnen de hoogte van je tarief beïnvloeden. Houd hier rekening mee bij het bepalen van je openingsbod voor de onderhandeling.</>
  );
  const ondergrensFestival = (
    <>Het starttarief vormt de <b>ondergrens</b> die past bij het niveau van de opdracht. Je <b>ervaring</b>, <b>reputatie</b> of <b>specialisme</b> en de <b>complexiteit</b> van de opdracht kunnen de hoogte van je tarief beïnvloeden. Houd hier rekening mee bij het bepalen van je openingsbod voor de onderhandeling.</>
  );
  const introAlineas =
    substap === "a"
      ? [
          <><b>Let op</b>: Als je als zzp-er werkt geeft deze tool na het doorlopen van alle stappen een onderbouwd <b>starttarief</b> (inclusief zzp-opslag) en een inschatting van de totale vergoeding voor je opdracht.</>,
          ondergrensFilmAv,
        ]
      : state.type === "festival"
        ? [
            <>Als je de tool stap voor stap doorloopt, krijg je een onderbouwd voorstel.</>,
            <>Werk je als zzp-er, dan geeft deze tool een onderbouwd <b>starttarief</b> (inclusief een zzp-opslag van 69,26%) en een inschatting van de totale vergoeding voor je werkzaamheden.</>,
            ondergrensFestival,
          ]
        : [
            <>Na het doorlopen van alle stappen geeft deze tool een onderbouwd <b>starttarief</b> (inclusief een zzp-opslag van 74,45%) en een inschatting van de totale vergoeding voor je opdracht.</>,
            ondergrensFilmAv,
          ];

  return (
    <div>
      {substap === "b" && (
        <div style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
          <button
            onClick={() => setSubstap("a")}
            aria-label="Terug naar type-keuze"
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "4px 12px 4px 9px", borderRadius: 6,
              background: FW.white, color: FW.navy, border: `1px solid ${FW.taupe}`,
              fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5,
              fontFamily: "var(--font-sans)", cursor: "pointer",
              transition: "background .15s",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = FW.beige2; }}
            onMouseLeave={e => { e.currentTarget.style.background = FW.white; }}
          >
            <i className="ri-arrow-left-line" style={{ fontSize: 14 }} /> Terug
          </button>
          <div style={{ padding: "4px 10px", borderRadius: 6,
            background: state.type === "film" ? FW.mint : FW.cyan, color: FW.navy, fontSize: 12, fontWeight: 700,
            textTransform: "uppercase", letterSpacing: 0.5 }}>{state.type === "film" ? "Film/AV-Productie" : "Filmfestival"}</div>
        </div>
      )}
      {substap === "a" && (
        <div style={{ marginBottom: 8, display: "inline-flex", gap: 8, alignItems: "center", color: FW.navy, opacity: .75 }}>
          <i className="ri-heart-line" style={{ fontSize: 18 }} />
          <span style={{ fontSize: 15, fontWeight: 600 }}>Waarmee kunnen we je helpen?</span>
        </div>
      )}
      <h1 className="step-hero" style={{ fontSize: 52, lineHeight: 1.1, margin: "8px 0 12px", letterSpacing: "-0.01em" }}>
        <span style={{ fontWeight: 700 }}>Bereken en onderbouw</span>{" "}
        <span style={{ fontWeight: 300 }}>stap voor stap een eerlijk openingsbod.</span>
      </h1>
      <div className="step-hero-sub" style={{ fontSize: 18, maxWidth: 720, color: FW.navy2, marginBottom: 32, lineHeight: 1.45 }}>
        {introAlineas.map((alinea, i) => (
          <p key={i} style={{ margin: i === introAlineas.length - 1 ? 0 : "0 0 12px" }}>{alinea}</p>
        ))}
      </div>

      {substap === "a" ? (
        <>
          <div style={{ fontSize: 14, fontWeight: 700, color: FW.navy, marginBottom: 12, textTransform: "uppercase", letterSpacing: 0.5, opacity: .75 }}>
            1. Waar ga je werken?
          </div>
          <div className="scope-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, maxWidth: 760 }}>
            <button onClick={() => pickType("film")} className="scope-card" style={scopeCard(FW.mint)} {...cardHoverProps(SCOPE_BASE_SHADOW)}>
              <IllusFilm />
              <div className="scope-card-title" style={{ fontSize: 24, fontWeight: 700, marginTop: 12 }}>Ik werk aan een<br/>filmproductie</div>
              <div style={{ fontSize: 14, opacity: .8, marginTop: 8 }}>Korte film, speelfilm, documentaire, animatie, innovatieve mediaproductie, serie</div>
              <div style={{ marginTop: "auto", paddingTop: 20, fontSize: 18, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 10 }}>
                Kies <span style={arrowWell()}>→</span>
              </div>
            </button>
            <button onClick={() => pickType("festival")} className="scope-card" style={scopeCard(FW.cyan)} {...cardHoverProps(SCOPE_BASE_SHADOW)}>
              <IllusFestival />
              <div className="scope-card-title" style={{ fontSize: 24, fontWeight: 700, marginTop: 12 }}>Ik werk aan een<br/>filmfestival</div>
              <div style={{ marginTop: "auto", paddingTop: 20, fontSize: 18, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 10 }}>
                Kies <span style={arrowWell()}>→</span>
              </div>
            </button>
          </div>
        </>
      ) : state.type === "film" ? (
        <>
          <div style={{ fontSize: 14, fontWeight: 700, color: FW.navy, marginBottom: 12, textTransform: "uppercase", letterSpacing: 0.5, opacity: .75 }}>
            2. Werk je als zzp'er of heb je een opdracht voor een zzp'er?
          </div>
          <div className="scope-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, maxWidth: 760 }}>
            <button onClick={() => pickPerspective("uitvoerder")} className="scope-card" style={vormCard()} {...cardHoverProps(VORM_BASE_SHADOW)}>
              <img src="/assets/illustration-zzper-filmproductie.svg" alt="" aria-hidden="true" style={{ width: 72, height: 72 }} />
              <div style={{ fontSize: 20, fontWeight: 700, marginTop: 10 }}>Ik werk als zzp'er</div>
              <div style={{ marginTop: "auto", paddingTop: 16, fontSize: 16, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 10 }}>
                Kies <span style={arrowWell()}>→</span>
              </div>
            </button>
            <button onClick={() => pickPerspective("opdrachtgever")} className="scope-card" style={vormCard()} {...cardHoverProps(VORM_BASE_SHADOW)}>
              <img src="/assets/illustration-opdrachtgever.svg" alt="" aria-hidden="true" style={{ width: 72, height: 72 }} />
              <div style={{ fontSize: 20, fontWeight: 700, marginTop: 10 }}>Ik ben opdrachtgever</div>
              <div style={{ marginTop: "auto", paddingTop: 16, fontSize: 16, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 10 }}>
                Kies <span style={arrowWell()}>→</span>
              </div>
            </button>
          </div>
        </>
      ) : (
        <FestivalVormKeuze onPick={pickVorm} />
      )}

      {substap === "b" && (
        <div className="step-nav" style={{ marginTop: 32, display: "flex", justifyContent: "space-between" }}>
          <FwButton variant="ghost" arrow={false} onClick={() => setSubstap("a")}>← Terug</FwButton>
        </div>
      )}
    </div>
  );
};

// FestivalVormKeuze: keuze zzp/loondienst voor festival.
// Hergebruikt door StepScope (substap b) én door de losse tool /tools/fair-pay-festival.
// Wordt opzettelijk los geëxporteerd zodat de losse tool geen "Terug naar type"-knop hoeft te tonen.
export const FestivalVormKeuze = ({ onPick, stapLabel = "2. Werk je als zzp'er of in loondienst?" }) => (
  <>
    {stapLabel && (
      <div style={{ fontSize: 14, fontWeight: 700, color: FW.navy, marginBottom: 12, textTransform: "uppercase", letterSpacing: 0.5, opacity: .75 }}>
        {stapLabel}
      </div>
    )}
    <div className="scope-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, maxWidth: 760 }}>
      <button onClick={() => onPick("zzp")} className="scope-card" style={vormCard()} {...cardHoverProps(VORM_BASE_SHADOW)}>
        <img src="/assets/illustration-zzper-filmfest.svg" alt="" aria-hidden="true" style={{ width: 72, height: 72 }} />
        <div style={{ fontSize: 20, fontWeight: 700, marginTop: 10 }}>Ik werk als zzp'er</div>
        <div style={{ marginTop: "auto", paddingTop: 16, fontSize: 16, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 10 }}>
          Kies <span style={arrowWell()}>→</span>
        </div>
      </button>
      <button onClick={() => onPick("loondienst")} className="scope-card" style={vormCard()} {...cardHoverProps(VORM_BASE_SHADOW)}>
        <img src="/assets/illustration-loondienst.svg" alt="" aria-hidden="true" style={{ width: 72, height: 72 }} />
        <div style={{ fontSize: 20, fontWeight: 700, marginTop: 10 }}>Ik werk in loondienst</div>
        <div style={{ marginTop: "auto", paddingTop: 16, fontSize: 16, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 10 }}>
          Kies <span style={arrowWell()}>→</span>
        </div>
      </button>
    </div>
  </>
);

const SCOPE_BASE_SHADOW = "0 4px 12px rgba(0,0,0,.06)";
const VORM_BASE_SHADOW = "0 2px 8px rgba(0,0,0,.04)";
const HOVER_SHADOW = "0 14px 30px rgba(0,0,0,.14)";

// Hover-feedback: kaart "tilt op" zodat 'ie als knop voelt; bij mouse-down een lichte druk-feedback.
const cardHoverProps = (baseShadow) => ({
  onMouseEnter: (e) => {
    e.currentTarget.style.transform = "translateY(-3px)";
    e.currentTarget.style.boxShadow = HOVER_SHADOW;
  },
  onMouseLeave: (e) => {
    e.currentTarget.style.transform = "translateY(0)";
    e.currentTarget.style.boxShadow = baseShadow;
  },
  onMouseDown: (e) => { e.currentTarget.style.transform = "translateY(-1px) scale(0.99)"; },
  onMouseUp:   (e) => { e.currentTarget.style.transform = "translateY(-3px)"; },
});

const scopeCard = (bg) => ({
  background: bg, border: "3px solid transparent",
  borderRadius: 24, padding: 28, textAlign: "left",
  color: FW.navy, cursor: "pointer", fontFamily: "var(--font-sans)",
  minHeight: 280, display: "flex", flexDirection: "column",
  transition: "transform .2s ease, box-shadow .2s ease",
  boxShadow: SCOPE_BASE_SHADOW,
});
const vormCard = () => ({
  background: FW.white,
  border: `1.5px solid ${FW.gray100}`,
  borderRadius: 24, padding: 24, textAlign: "left",
  color: FW.navy, cursor: "pointer", fontFamily: "var(--font-sans)",
  minHeight: 180, display: "flex", flexDirection: "column",
  transition: "transform .2s ease, box-shadow .2s ease",
  boxShadow: VORM_BASE_SHADOW,
});
const arrowWell = () => ({
  display: "inline-grid", placeItems: "center",
  width: 40, height: 40, borderRadius: 999,
  background: FW.navy, color: "#fff", fontSize: 18,
});

const IllusFilm = () => (
  <img src="/assets/illustration-filmproductie.svg" alt="" aria-hidden="true" style={{ width: 160, height: "auto" }} />
);
const IllusFestival = () => (
  <img src="/assets/illustration-filmfestival.svg" alt="" aria-hidden="true" style={{ width: 160, height: "auto" }} />
);
